import { acquireConsumer, releaseConsumer } from '@shared/actionCableConsumer'

const listeners = new Set()
const state = {
    klip: new Map(),
    streamer_klip: new Map(),
}
let version = 0
let snapshot = {
    klip: state.klip,
    streamer_klip: state.streamer_klip,
    version,
}

const subscriptionEntries = new Map()

const notify = () => {
    version += 1
    snapshot = {
        klip: state.klip,
        streamer_klip: state.streamer_klip,
        version,
    }
    listeners.forEach((listener) => listener())
}

const seedIfMissing = (map, id, value) => {
    if (!id || value === undefined || value === null) {
        return
    }

    if (!map.has(id)) {
        map.set(id, value)
    }
}

const channelForType = (resourceType) => {
    if (resourceType === 'streamer_klip') {
        return 'StreamerKlipPlayCountsChannel'
    }

    if (resourceType === 'klip') {
        return 'KlipLibraryPlayCountsChannel'
    }
}

const subscriptionKeyFor = (actionCableUrl, resourceType) => {
    const channelName = channelForType(resourceType)
    if (!actionCableUrl || !channelName) {
        return null
    }

    return `${channelName}:${actionCableUrl}`
}

const retainSubscription = (actionCableUrl, resourceType) => {
    if (!actionCableUrl) {
        return () => {}
    }

    const key = subscriptionKeyFor(actionCableUrl, resourceType)
    if (!key) {
        return () => {}
    }

    const existingEntry = subscriptionEntries.get(key)
    if (existingEntry) {
        existingEntry.refCount += 1
        return () => releaseSubscription(key)
    }

    const consumer = acquireConsumer(actionCableUrl)
    const entry = {
        actionCableUrl,
        connected: false,
        refCount: 1,
        subscription: null,
    }
    const subscription = consumer.subscriptions.create(
        { channel: channelForType(resourceType) },
        {
            connected: () => {
                entry.connected = true
                notify()
            },
            disconnected: () => {
                entry.connected = false
                notify()
            },
            rejected: () => {
                entry.connected = false
                console.warn(`${channelForType(resourceType)} subscription rejected`)
                notify()
            },
            received: (data) => {
                if (data.type !== 'klip_play_count_updated') {
                    return
                }
                
                const map = state[resourceType]
                if (map) {
                    map.set(data[resourceType].id, data[resourceType].play_count)
                }

                notify()
            },
        },
    )

    entry.subscription = subscription
    subscriptionEntries.set(key, entry)

    return () => releaseSubscription(key)
}

export const klipPlayCountStore = {
    subscribe: (listener) => {
        listeners.add(listener)
        return () => listeners.delete(listener)
    },
    getSnapshot: () => snapshot,
    retainSubscription,
    seedInitialCounts: ({ resourceType, resourceId, playCount }) => {
        const map = state[resourceType]
        if (map) {
            seedIfMissing(map, resourceId, playCount)
        }
    },
    isConnected: (actionCableUrl, resourceType) => {
        const key = subscriptionKeyFor(actionCableUrl, resourceType)
        const entry = key ? subscriptionEntries.get(key) : null
        return !!entry?.connected
    },
}

const releaseSubscription = (key) => {
    const entry = subscriptionEntries.get(key)
    if (!entry) {
        return
    }

    entry.refCount -= 1
    if (entry.refCount > 0) {
        return
    }

    entry.connected = false
    entry.subscription.unsubscribe()
    subscriptionEntries.delete(key)
    releaseConsumer(entry.actionCableUrl)
    notify()
}
